/**
 * The trail, rendered and described.
 *
 * Two jobs at once, which is why it is one component: it shows a person where they are
 * and gives them one click back, and it emits the `BreadcrumbList` that Google renders
 * in place of the raw URL in a search result. Building the visual trail and the schema
 * separately means they drift, and the schema is the half nobody looks at.
 *
 * The last crumb is the current page and is NOT a link — a link to where you already
 * are is a dead control, and screen readers announce it as a navigation option that
 * does nothing.
 */

import { Link } from 'react-router-dom';

export interface Crumb {
  name: string;
  path: string;
}

export function Breadcrumbs({ trail }: { trail: Crumb[] }) {
  if (trail.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="crumbs">
      <ol>
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li key={crumb.path}>
              {isLast ? (
                <span aria-current="page">{crumb.name}</span>
              ) : (
                <>
                  <Link to={crumb.path}>{crumb.name}</Link>
                  <span className="crumbs__sep" aria-hidden="true">/</span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** The trail every public page starts from. */
export const HOME_CRUMB: Crumb = { name: 'Home', path: '/' };
